import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { listServices } from "@/lib/services.functions";
import { createBooking, recordBookingEvidence } from "@/lib/bookings.functions";
import { listHomes } from "@/lib/homes.functions";
import { formatTemplateAnswers, templateFor } from "@/lib/dimension-templates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ServicePicker } from "@/components/booking/ServicePicker";
import { AppHeader } from "@/components/site/AppHeader";
import { removeUploadedFile, uploadPhoto } from "@/lib/uploads";
import { PageShell } from "@/components/ui/shields-layout";

const servicesQuery = queryOptions({ queryKey: ["services"], queryFn: () => listServices() });
const homesQuery = queryOptions({ queryKey: ["homes"], queryFn: () => listHomes() });

export const Route = createFileRoute("/_authenticated/book")({
  validateSearch: (search: Record<string, unknown>): { service?: string; homeId?: string } => ({
    ...(typeof search["service"] === "string" ? { service: search["service"] as string } : {}),
    ...(typeof search["homeId"] === "string" ? { homeId: search["homeId"] as string } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Get something done — Shields" },
      { name: "description", content: "Tell Shields what needs doing and post a privacy-safe request to suitable local Partners." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookPage,
});

const PROPERTY_TYPES = ["house", "bungalow", "flat", "commercial", "other"];
const WINDOWS = ["Morning (8–12)", "Afternoon (12–5)", "Evening (5–8)", "Flexible"];
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

interface Draft {
  homeId: string;
  serviceId: string;
  description: string;
  propertyType: string;
  maintenanceCondition: string;
  sizeEstimate: string;
  lengthM: string;
  widthM: string;
  heightM: string;
  dimensionUnit: "m" | "ft";
  accessNotes: string;
  hazards: string;
  addressLine: string;
  townCity: string;
  postcode: string;
  preferredDate: string;
  preferredWindow: string;
  altDate: string;
  altWindow: string;
}

const EMPTY: Draft = {
  homeId: "",
  serviceId: "",
  description: "",
  propertyType: "house",
  maintenanceCondition: "",
  sizeEstimate: "",
  lengthM: "",
  widthM: "",
  heightM: "",
  dimensionUnit: "m",
  accessNotes: "",
  hazards: "",
  addressLine: "",
  townCity: "",
  postcode: "",
  preferredDate: "",
  preferredWindow: "",
  altDate: "",
  altWindow: "",
};

const SIZE_OPTIONS: [string, string][] = [
  ["small", "Small — one item or a quick repair"],
  ["medium", "Medium — around half a day"],
  ["large", "Large — around a full day"],
  ["very_large", "Very large — multi-day or whole property"],
];

const STEP_TITLES = ["What needs doing?", "Tell us about it", "Where & when?", "Check & post"];

function BookPage() {
  const { data: services } = useSuspenseQuery(servicesQuery);
  const { data: homes } = useSuspenseQuery(homesQuery);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const { service: presetSlug, homeId: requestedHomeId } = Route.useSearch();
  const [draft, setDraft] = useState<Draft>(() => {
    const preset = presetSlug ? services.find((service) => service.slug === presetSlug) : undefined;
    const activeHome = homes.find((home) => home.id === requestedHomeId)
      ?? homes.find((home) => home.isActive)
      ?? homes[0];
    return {
      ...EMPTY,
      serviceId: preset?.id ?? "",
      homeId: activeHome?.id ?? "",
      addressLine: activeHome?.addressLine ?? "",
      townCity: activeHome?.townCity ?? "",
      postcode: activeHome?.postcode ?? "",
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const [templateAnswers, setTemplateAnswers] = useState<Record<string, string>>({});
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  const selectedService = services.find((service) => service.id === draft.serviceId);
  const minDate = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const template = templateFor(selectedService?.slug);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const stepValid =
    step === 0
      ? Boolean(draft.serviceId)
      : step === 1
        ? draft.description.trim().length >= 20
        : step === 2
          ? UK_POSTCODE.test(draft.postcode.trim()) && Boolean(draft.preferredDate) && Boolean(draft.preferredWindow) && (!draft.altWindow || Boolean(draft.altDate))
          : true;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await createBooking({
        data: {
          homeId: draft.homeId || undefined,
          serviceId: draft.serviceId,
          description:
            draft.description.trim() +
            formatTemplateAnswers(templateFor(selectedService?.slug), templateAnswers),
          propertyType: draft.propertyType,
          maintenanceCondition: draft.maintenanceCondition || undefined,
          sizeEstimate: draft.sizeEstimate || undefined,
          lengthM: draft.lengthM ? Number(draft.lengthM) : undefined,
          widthM: draft.widthM ? Number(draft.widthM) : undefined,
          heightM: draft.heightM ? Number(draft.heightM) : undefined,
          dimensionUnit: draft.dimensionUnit,
          accessNotes: draft.accessNotes || undefined,
          hazards: draft.hazards || undefined,
          pricingMode: draft.lengthM || draft.widthM || draft.heightM ? "onsite" : "brief",
          addressLine: draft.addressLine || undefined,
          townCity: draft.townCity || undefined,
          postcode: draft.postcode.trim(),
          preferredDate: draft.preferredDate || undefined,
          preferredWindow: draft.preferredWindow || undefined,
          altDate: draft.altDate || undefined,
          altWindow: draft.altWindow || undefined,
          adultConfirmed,
          idempotencyKey,
        },
      });
      let uploaded = 0;
      for (const file of mediaFiles) {
        try {
          const path = await uploadPhoto("job-evidence", file);
          const evidence = await recordBookingEvidence({
            data: {
              bookingId: result.id,
              path,
              kind: file.type.startsWith("video/") ? "video" : "photo",
            },
          });
          if (!evidence.ok) {
            await removeUploadedFile("job-evidence", path);
            throw new Error(evidence.error);
          }
          uploaded += 1;
        } catch (mediaError) {
          console.error("[Booking] customer media upload failed", mediaError);
          toast.error(`Your request was posted, but ${file.name} could not be attached.`);
        }
      }
      if (result.matchingIssue) {
        toast.warning(
          uploaded
            ? `Request posted with ${uploaded} attachment${uploaded === 1 ? "" : "s"}. Shields is reviewing matching.`
            : "Request posted. Shields is reviewing matching.",
        );
      } else {
        toast.success(uploaded ? `Request posted with ${uploaded} attachment${uploaded === 1 ? "" : "s"}` : "Request posted to suitable Partners");
      }
      navigate({ to: "/bookings/$bookingId", params: { bookingId: result.id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not post your request");
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <AppHeader workspace="customer" />
      <PageShell width="narrow" className="pb-44 sm:pb-28">
        <header>
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gold">
            Step {step + 1} of {STEP_TITLES.length}
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {STEP_TITLES[step]}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {step === 0 && "Choose the service. You can explain the job on the next screen."}
            {step === 1 && "Give Partners enough information to understand the work. Extra details are optional."}
            {step === 2 && "Tell Shields where the job is and when you would prefer it done."}
            {step === 3 && "Check the useful details before Shields sends your privacy-safe request."}
          </p>
        </header>

        <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${((step + 1) / STEP_TITLES.length) * 100}%` }}
          />
        </div>

        <ol className="mt-4 flex gap-2" aria-label="Booking progress">
          {STEP_TITLES.map((title, index) => (
            <li key={title} className="flex flex-1 items-center gap-2">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                index < step
                  ? "bg-success text-success-foreground"
                  : index === step
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
              }`}>
                {index < step ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
              </span>
              <span className={`hidden text-xs font-semibold sm:block ${index === step ? "text-foreground" : "text-muted-foreground"}`}>
                {title}
              </span>
            </li>
          ))}
        </ol>

        {error && (
          <p role="alert" className="mt-6 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
            {error}
          </p>
        )}

        <section key={step} className="card-surface mt-6 p-5 sm:p-7">
          {step === 0 && (
            <ServicePicker
              services={services}
              value={draft.serviceId}
              onChange={(id) => {
                set("serviceId", id);
                setTemplateAnswers({});
              }}
            />
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="description">What needs doing?</Label>
                <Textarea
                  id="description"
                  value={draft.description}
                  onChange={(event) => set("description", event.target.value)}
                  rows={5}
                  minLength={20}
                  maxLength={4000}
                  placeholder="e.g. The shower head is leaking from the fitting and needs repairing or replacing"
                  className="mt-1.5"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Please do not include phone numbers or email addresses. Partners contact you through Shields.
                </p>
              </div>

              <div>
                <Label htmlFor="propertyType">Property type</Label>
                <select
                  id="propertyType"
                  value={draft.propertyType}
                  onChange={(event) => set("propertyType", event.target.value)}
                  className="mt-1.5 min-h-12 w-full rounded-lg border border-input bg-background px-3 text-sm"
                >
                  {PROPERTY_TYPES.map((type) => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><Camera className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-bold">Photos or video (optional)</p>
                    <p className="mt-1 text-xs text-muted-foreground">Add up to 8 useful files. Partners can use them to understand the work without seeing unnecessary personal information.</p>
                  </div>
                </div>
                <Input
                  id="booking-media"
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm"
                  className="mt-4"
                  onChange={(event) => {
                    const selected = Array.from(event.target.files ?? []);
                    const next = [...mediaFiles, ...selected].slice(0, 8);
                    setMediaFiles(next);
                    event.currentTarget.value = "";
                  }}
                />
                {mediaFiles.length > 0 && (
                  <div className="mt-3 grid gap-2">
                    {mediaFiles.map((file, index) => (
                      <div key={file.name + file.lastModified + index} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm">
                        <span className="min-w-0 truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setMediaFiles((files) => files.filter((_, itemIndex) => itemIndex !== index))}
                          className="rounded-md p-1 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {template.questions.length > 0 && (
                <div className="rounded-xl border border-border bg-muted/25 p-4">
                  <p className="text-sm font-bold">{selectedService?.name ?? "Service"} details</p>
                  <p className="mt-1 text-xs text-muted-foreground">A few useful details can help Partners quote more accurately.</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {template.questions.map((question) => (
                      <div key={question.id}>
                        <Label htmlFor={`tpl-${question.id}`}>{question.label}</Label>
                        <Input
                          id={`tpl-${question.id}`}
                          value={templateAnswers[question.id] ?? ""}
                          onChange={(event) =>
                            setTemplateAnswers((answers) => ({ ...answers, [question.id]: event.target.value }))
                          }
                          maxLength={200}
                          placeholder={question.placeholder}
                          className="mt-1.5 min-h-11"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <details className="rounded-xl border border-border">
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-bold">
                  Add extra job details <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </summary>
                <div className="grid gap-4 border-t border-border p-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="condition">Condition (optional)</Label>
                      <Input id="condition" value={draft.maintenanceCondition} onChange={(e) => set("maintenanceCondition", e.target.value)} placeholder="e.g. recurring leak" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="sizeEstimate">Approximate size (optional)</Label>
                      <select id="sizeEstimate" value={draft.sizeEstimate} onChange={(e) => set("sizeEstimate", e.target.value)} className="mt-1.5 min-h-11 w-full rounded-lg border border-input bg-background px-3 text-sm">
                        <option value="">Not sure</option>
                        {SIZE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </div>
                  </div>

                  {(template.length || template.width || template.height) && (
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold">Measurements (optional)</p>
                          <p className="text-xs text-muted-foreground">{template.hint}</p>
                        </div>
                        <div className="flex overflow-hidden rounded-lg border border-border">
                          {(["m", "ft"] as const).map((unit) => (
                            <button
                              key={unit}
                              type="button"
                              onClick={() => set("dimensionUnit", unit)}
                              className={`min-h-10 px-3 text-sm font-bold ${draft.dimensionUnit === unit ? "bg-primary text-primary-foreground" : "bg-background"}`}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-3">
                        {([
                          ["lengthM", template.length],
                          ["widthM", template.width],
                          ["heightM", template.height],
                        ] as const).filter(([, label]) => Boolean(label)).map(([key, label]) => (
                          <div key={key}>
                            <Label htmlFor={key}>{label} ({draft.dimensionUnit})</Label>
                            <Input id={key} type="number" min="0" step="0.1" value={draft[key]} onChange={(e) => set(key, e.target.value)} className="mt-1.5" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="accessNotes">Access notes (optional)</Label>
                      <Textarea id="accessNotes" value={draft.accessNotes} onChange={(e) => set("accessNotes", e.target.value)} rows={2} maxLength={2000} placeholder="e.g. side gate at the rear" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="hazards">Anything the Partner should know? (optional)</Label>
                      <Textarea id="hazards" value={draft.hazards} onChange={(e) => set("hazards", e.target.value)} rows={2} maxLength={2000} placeholder="e.g. overhead cables near the work area" className="mt-1.5" />
                    </div>
                  </div>
                </div>
              </details>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              {homes.length > 0 && (
                <div>
                  <Label htmlFor="homeId">Property</Label>
                  <select
                    id="homeId"
                    value={draft.homeId}
                    onChange={(event) => {
                      const id = event.target.value;
                      const home = homes.find((item) => item.id === id);
                      setDraft((current) => ({
                        ...current,
                        homeId: id,
                        addressLine: home?.addressLine ?? (id ? "" : current.addressLine),
                        townCity: home?.townCity ?? (id ? "" : current.townCity),
                        postcode: home?.postcode ?? (id ? "" : current.postcode),
                      }));
                    }}
                    className="mt-1.5 min-h-12 w-full rounded-lg border border-input bg-background px-3 text-sm"
                  >
                    {homes.map((home) => <option key={home.id} value={home.id}>{home.label} · {home.postcode}</option>)}
                    <option value="">Use a different address</option>
                  </select>
                </div>
              )}
              <p className="rounded-xl bg-primary-soft px-4 py-3 text-sm text-muted-foreground">
                Partners see your general area before assignment. Your exact address and contact details stay private until the appropriate stage.
              </p>
              {draft.homeId ? (
                <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
                  <strong>{homes.find((home) => home.id === draft.homeId)?.label ?? "Saved property"}</strong>
                  <p className="mt-1 text-muted-foreground">
                    {[draft.addressLine, draft.townCity, draft.postcode].filter(Boolean).join(", ")}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">Partners only see the general area until the appropriate assignment stage.</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input id="postcode" value={draft.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} placeholder="e.g. M1 2AB" autoComplete="postal-code" required className="mt-1.5 min-h-12" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="addressLine">Address line (optional now)</Label>
                      <Input id="addressLine" value={draft.addressLine} onChange={(e) => set("addressLine", e.target.value)} autoComplete="address-line1" className="mt-1.5 min-h-12" />
                    </div>
                    <div>
                      <Label htmlFor="townCity">Town / city (optional)</Label>
                      <Input id="townCity" value={draft.townCity} onChange={(e) => set("townCity", e.target.value)} autoComplete="address-level2" className="mt-1.5 min-h-12" />
                    </div>
                  </div>
                </>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="preferredDate">Preferred date</Label>
                  <Input id="preferredDate" type="date" min={minDate} value={draft.preferredDate} onChange={(e) => set("preferredDate", e.target.value)} className="mt-1.5 min-h-12" />
                </div>
                <div>
                  <Label htmlFor="preferredWindow">Preferred time</Label>
                  <select id="preferredWindow" value={draft.preferredWindow} onChange={(e) => set("preferredWindow", e.target.value)} className="mt-1.5 min-h-12 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    <option value="">Any time</option>
                    {WINDOWS.map((window) => <option key={window} value={window}>{window}</option>)}
                  </select>
                </div>
              </div>
              <details className="rounded-xl border border-border">
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-bold">
                  Add an alternative date <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </summary>
                <div className="grid gap-4 border-t border-border p-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="altDate">Alternative date</Label>
                    <Input id="altDate" type="date" min={minDate} value={draft.altDate} onChange={(e) => set("altDate", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="altWindow">Alternative time</Label>
                    <select id="altWindow" value={draft.altWindow} onChange={(e) => set("altWindow", e.target.value)} className="mt-1.5 min-h-11 w-full rounded-lg border border-input bg-background px-3 text-sm">
                      <option value="">Any time</option>
                      {WINDOWS.map((window) => <option key={window} value={window}>{window}</option>)}
                    </select>
                  </div>
                </div>
              </details>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <dl className="grid gap-3 text-sm">
                {[
                  ["Service", selectedService?.name ?? "—"],
                  ["What needs doing", draft.description],
                  ["Property", draft.propertyType],
                  ["Postcode", draft.postcode],
                  draft.preferredDate && ["Preferred", `${draft.preferredDate}${draft.preferredWindow ? ` · ${draft.preferredWindow}` : ""}`],
                  draft.altDate && ["Alternative", `${draft.altDate}${draft.altWindow ? ` · ${draft.altWindow}` : ""}`],
                ].filter(Boolean).map((row) => {
                  const [label, value] = row as [string, string];
                  return (
                    <div key={label} className="grid gap-1 rounded-xl border border-border px-4 py-3 sm:grid-cols-[10rem_1fr]">
                      <dt className="font-semibold text-muted-foreground">{label}</dt>
                      <dd className="whitespace-pre-wrap font-medium">{value}</dd>
                    </div>
                  );
                })}
              </dl>

              <p className="rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">
                Posting is free. Customers deal in GBP. Suitable Partners receive the job details and general area, and you stay in control of who you choose.
              </p>

              <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border border-border px-4 py-3 text-sm">
                <input type="checkbox" checked={adultConfirmed} onChange={(e) => setAdultConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
                <span>
                  I confirm I’m 18 or over and authorised to book work at this property.
                  <span className="block text-xs text-muted-foreground">Shields stores the confirmation, not your date of birth.</span>
                </span>
              </label>
            </div>
          )}

          <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t border-border bg-card/98 px-4 py-3 shadow-[0_-10px_30px_-24px_rgba(6,27,58,.45)] backdrop-blur sm:static sm:mt-7 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none sm:backdrop-blur-none">
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setStep((current) => Math.max(0, current - 1))}
                disabled={step === 0 || busy}
                className="min-h-12 min-w-[7rem] rounded-xl"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>

              {step < STEP_TITLES.length - 1 ? (
                <Button
                  onClick={() => setStep((current) => current + 1)}
                  disabled={!stepValid}
                  className="min-h-12 min-w-[8.5rem] rounded-xl bg-[#e1b64f] font-extrabold text-foreground hover:bg-[#d8a82f] disabled:bg-[#e8edf3] disabled:text-[#8a98a8]"
                >
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={busy || !adultConfirmed}
                  className="min-h-12 min-w-[10.5rem] rounded-xl bg-[#e1b64f] font-extrabold text-foreground hover:bg-[#d8a82f] disabled:bg-[#e8edf3] disabled:text-[#8a98a8]"
                >
                  {busy ? "Posting…" : "Complete & Post"}
                  {!busy && <ArrowRight className="ml-1 h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>
        </section>
      </PageShell>
    </div>
  );
}
